/**
 * DevSimulatorContext
 *
 * Context globale per il sistema di simulazione dati form.
 * Gestisce:
 * - Stato abilitazione (persistito in localStorage)
 * - Registry dei form registrati
 * - Form attualmente attivo
 *
 * @author Salvo de Luca - Dialogicom s.r.l.s.
 * @version 1.0.0
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode
} from 'react';

// ============================================
// TYPES
// ============================================

export interface ApplyDataOptions {
  /** Se abilitare l'effetto typing (digitazione progressiva) */
  typingEffect?: boolean;
  /** Velocità typing in ms per carattere (default: 30) */
  typingSpeed?: number;
}

export interface SimulatorFormConfig {
  /** ID univoco del form */
  id: string;
  /** Nome visualizzato */
  displayName: string;
  /** Descrizione breve */
  description?: string;
  /** Opzioni disponibili per questo form */
  options?: SimulatorOption[];
  /** Callback per generare dati simulati */
  generateData: (selectedOptions: Record<string, string>) => Record<string, any>;
  /** Callback per applicare i dati al form */
  applyData: (data: Record<string, any>, options?: ApplyDataOptions) => void;
}

export interface SimulatorOption {
  /** ID opzione */
  id: string;
  /** Label visualizzata */
  label: string;
  /** Tipo di input */
  type: 'select' | 'toggle';
  /** Valori disponibili (per select) */
  values?: { value: string; label: string }[];
  /** Valore default */
  defaultValue: string;
}

export interface DevSimulatorContextValue {
  /** Se il simulatore è abilitato globalmente */
  isEnabled: boolean;
  /** Abilita/disabilita il simulatore */
  setEnabled: (enabled: boolean) => void;
  /** Toggle stato */
  toggleEnabled: () => void;
  /** Se il simulatore parte abilitato all'avvio */
  autoStartEnabled: boolean;
  /** Imposta auto-start */
  setAutoStartEnabled: (enabled: boolean) => void;
  /** Form attualmente registrati */
  registeredForms: Map<string, SimulatorFormConfig>;
  /** Form attivo (quello corrente nella pagina) */
  activeFormId: string | null;
  /** Registra un form */
  registerForm: (config: SimulatorFormConfig) => void;
  /** Deregistra un form */
  unregisterForm: (formId: string) => void;
  /** Imposta il form attivo */
  setActiveForm: (formId: string | null) => void;
  /** Genera e applica dati simulati per il form attivo */
  simulateActiveForm: (options?: Record<string, string>) => void;
  /** Se il pannello opzioni è aperto */
  isPanelOpen: boolean;
  /** Apri/chiudi pannello */
  setPanelOpen: (open: boolean) => void;
  /** Toggle pannello */
  togglePanel: () => void;
  /** Modale di conferma attivazione/disattivazione */
  confirmModal: { visible: boolean; message: string; isEnabling: boolean };
  /** Chiudi modale conferma */
  closeConfirmModal: () => void;
}

// ============================================
// CONSTANTS
// ============================================

const STORAGE_KEY = 'coronatek_dev_simulator_enabled';
const AUTOSTART_KEY = 'coronatek_dev_simulator_autostart';

// ============================================
// CONTEXT
// ============================================

const DevSimulatorContext = createContext<DevSimulatorContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

interface DevSimulatorProviderProps {
  children: ReactNode;
}

export const DevSimulatorProvider: React.FC<DevSimulatorProviderProps> = ({ children }) => {
  // Stato abilitazione (persistito in localStorage)
  // All'avvio controlla autoStart: se true, forza enabled a true
  const [isEnabled, setIsEnabledState] = useState<boolean>(() => {
    try {
      const autoStart = localStorage.getItem(AUTOSTART_KEY);
      if (autoStart === 'true') return true;
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Auto-start config (persistito)
  const [autoStartEnabled, setAutoStartEnabledState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AUTOSTART_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Registry dei form
  const [registeredForms, setRegisteredForms] = useState<Map<string, SimulatorFormConfig>>(
    () => new Map()
  );

  // Form attivo
  const [activeFormId, setActiveFormId] = useState<string | null>(null);

  // Pannello opzioni
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Modale conferma attivazione/disattivazione
  const [confirmModal, setConfirmModal] = useState<{ visible: boolean; message: string; isEnabling: boolean }>({
    visible: false,
    message: '',
    isEnabling: false
  });

  const closeConfirmModal = useCallback(() => {
    setConfirmModal(prev => ({ ...prev, visible: false }));
  }, []);

  // Persisti auto-start
  const setAutoStartEnabled = useCallback((enabled: boolean) => {
    setAutoStartEnabledState(enabled);
    try {
      localStorage.setItem(AUTOSTART_KEY, String(enabled));
    } catch (e) {
      console.warn('[DevSimulator] Impossibile salvare autoStart in localStorage:', e);
    }
  }, []);

  // Persisti stato abilitazione
  const setEnabled = useCallback((enabled: boolean) => {
    setIsEnabledState(enabled);
    try {
      localStorage.setItem(STORAGE_KEY, String(enabled));
    } catch (e) {
      console.warn('[DevSimulator] Impossibile salvare stato in localStorage:', e);
    }

    // Se disabilitato, chiudi pannello
    if (!enabled) {
      setIsPanelOpen(false);
    }
  }, []);

  const toggleEnabled = useCallback(() => {
    const newState = !isEnabled;
    setEnabled(newState);
    // Mostra modale di conferma con stile
    setConfirmModal({
      visible: true,
      message: newState
        ? 'Simulatore Dati ABILITATO. Vai su una pagina con un form per vedere il pannello opzioni.'
        : 'Simulatore Dati DISABILITATO. Il pannello non apparirà più.',
      isEnabling: newState
    });
  }, [isEnabled, setEnabled]);

  // Registra form
  const registerForm = useCallback((config: SimulatorFormConfig) => {
    console.log('[DevSimulator] Registrazione form:', config.id);
    setRegisteredForms(prev => {
      const newMap = new Map(prev);
      newMap.set(config.id, config);
      return newMap;
    });
    // Auto-imposta come form attivo
    setActiveFormId(config.id);
  }, []);

  // Deregistra form
  const unregisterForm = useCallback((formId: string) => {
    console.log('[DevSimulator] Deregistrazione form:', formId);
    setRegisteredForms(prev => {
      const newMap = new Map(prev);
      newMap.delete(formId);
      return newMap;
    });
    // Se era il form attivo, resetta
    setActiveFormId(prev => prev === formId ? null : prev);
  }, []);

  // Imposta form attivo
  const setActiveForm = useCallback((formId: string | null) => {
    setActiveFormId(formId);
  }, []);

  // Simula form attivo
  const simulateActiveForm = useCallback((options?: Record<string, string>) => {
    if (!activeFormId) {
      console.warn('[DevSimulator] Nessun form attivo');
      return;
    }

    const formConfig = registeredForms.get(activeFormId);
    if (!formConfig) {
      console.warn('[DevSimulator] Form non trovato:', activeFormId);
      return;
    }

    // Estrai opzioni speciali (prefisso _)
    const typingEffect = options?._typingEffect === 'true';

    // Filtra opzioni speciali prima di passarle al generatore
    const formOptions: Record<string, string> = {};
    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        if (!key.startsWith('_')) {
          formOptions[key] = value;
        }
      });
    }

    // Merge opzioni con default
    const defaultOptions: Record<string, string> = {};
    formConfig.options?.forEach(opt => {
      defaultOptions[opt.id] = opt.defaultValue;
    });
    const mergedOptions = { ...defaultOptions, ...formOptions };

    console.log('[DevSimulator] Generazione dati per:', activeFormId, 'opzioni:', mergedOptions, 'typing:', typingEffect);

    // Genera dati
    const simulatedData = formConfig.generateData(mergedOptions);

    // Applica dati con opzioni
    formConfig.applyData(simulatedData, { typingEffect });

    // Chiudi pannello
    setIsPanelOpen(false);

    console.log('[DevSimulator] Dati applicati:', Object.keys(simulatedData).length, 'campi', typingEffect ? '(con typing effect)' : '');
  }, [activeFormId, registeredForms]);

  // Toggle pannello
  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev);
  }, []);

  // Chiudi pannello se non ci sono form
  useEffect(() => {
    if (registeredForms.size === 0 && isPanelOpen) {
      setIsPanelOpen(false);
    }
  }, [registeredForms.size, isPanelOpen]);

  // ============================================
  // FUNZIONI GLOBALI per accesso dal menu statico
  // ============================================
  useEffect(() => {
    (window as any).toggleDevSimulator = toggleEnabled;
    (window as any).getDevSimulatorState = () => isEnabled;

    return () => {
      delete (window as any).toggleDevSimulator;
      delete (window as any).getDevSimulatorState;
    };
  }, [isEnabled, toggleEnabled]);

  // Context value memoizzato
  const value = useMemo<DevSimulatorContextValue>(() => ({
    isEnabled,
    setEnabled,
    toggleEnabled,
    autoStartEnabled,
    setAutoStartEnabled,
    registeredForms,
    activeFormId,
    registerForm,
    unregisterForm,
    setActiveForm,
    simulateActiveForm,
    isPanelOpen,
    setPanelOpen: setIsPanelOpen,
    togglePanel,
    confirmModal,
    closeConfirmModal
  }), [
    isEnabled,
    setEnabled,
    toggleEnabled,
    autoStartEnabled,
    setAutoStartEnabled,
    registeredForms,
    activeFormId,
    registerForm,
    unregisterForm,
    setActiveForm,
    simulateActiveForm,
    isPanelOpen,
    togglePanel,
    confirmModal,
    closeConfirmModal
  ]);

  return (
    <DevSimulatorContext.Provider value={value}>
      {children}
    </DevSimulatorContext.Provider>
  );
};

// ============================================
// HOOK
// ============================================

/**
 * Hook per accedere al DevSimulator context
 */
export function useDevSimulator(): DevSimulatorContextValue {
  const context = useContext(DevSimulatorContext);

  if (!context) {
    throw new Error('useDevSimulator must be used within a DevSimulatorProvider');
  }

  return context;
}

/**
 * Hook per registrare un form nel DevSimulator
 *
 * @example
 * ```tsx
 * useDevSimulatorForm({
 *   id: 'business-card-edit',
 *   displayName: 'Compila Dati Cliente',
 *   options: [...],
 *   generateData: (opts) => generateBusinessCardData(opts),
 *   applyData: (data) => setFormData(data)
 * });
 * ```
 */
export function useDevSimulatorForm(config: SimulatorFormConfig, active: boolean = true): void {
  const { registerForm, unregisterForm, isEnabled } = useDevSimulator();

  useEffect(() => {
    // Non registrare se il simulatore è disabilitato O se questo form non è attivo
    // Utile per modali che hanno multiple istanze - solo quella aperta deve registrarsi
    if (!isEnabled || !active) return;

    registerForm(config);

    return () => {
      unregisterForm(config.id);
    };
  // IMPORTANTE: Includi config e active nelle dipendenze per aggiornare le closure
  // quando currentFormType o altre dipendenze cambiano in CustomerRegistrationForm.
  // Senza questo, applySimulatedData mantiene valori stale di currentFormType.
  }, [isEnabled, active, config, registerForm, unregisterForm]);
}

// ============================================
// EXPORTS
// ============================================

export default DevSimulatorContext;
